import { useEffect, useRef } from "react";
import { Alert, AlertTitle, Box, Chip, Typography } from "@mui/material";

// A receipt describes the acknowledged write; translation completion stays separate.
export default function MedicineSaveFeedback({ receipt, error }) {
  const receiptRef = useRef(null);
  useEffect(() => {
    if (receipt) receiptRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [receipt]);
  if (error) return <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>;
  if (!receipt) return null;
  return <Alert ref={receiptRef} severity="success" sx={{ mb: 2, scrollMarginTop: 180 }} role="status">
    <AlertTitle>{receipt.created ? "Medicine added successfully" : "Changes saved successfully"}</AlertTitle>
    <Typography variant="body2"><strong>{receipt.name}</strong> · {receipt.language.toUpperCase()} · {receipt.visible ? "Published" : "Hidden from storefront"}</Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
      {receipt.sections.map(section => <Chip key={section} label={section} size="small" />)}
    </Box>
    {receipt.translation && <Typography variant="body2" sx={{ mt: 1 }}>Translation requested. Follow its progress below or on the dashboard.</Typography>}
    {receipt.warning && <Typography variant="body2" color="warning.dark" sx={{mt:1}}>{receipt.warning}</Typography>}
    <Typography variant="caption">Saved at {new Date(receipt.at).toLocaleTimeString()}. You can continue editing this medicine.</Typography>
  </Alert>;
}

function comparable(value) {
  if (Array.isArray(value)) return value.map(comparable);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !["_id", "__v", "createdAt", "updatedAt", "file"].includes(key))
    .sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => [key, comparable(entry)]));
  return value;
}
export function medicineSaveReceipt(data, before, language, translation, created = false) {
  const groups = {
    "Identification": ["genericBrand", "generic", "originator", "companyId"],
    "Categories": ["subCatId"],
    "Media": ["images", "videos"],
    "Variations & pricing": ["variants"],
    "SEO": ["metaKeyword", "pageTitle", "metaTitle", "metaDesc", "tags"],
    "Additional sections": ["additionalSectionList"],
    "Publication & stock": ["visible", "availability", "prescribed", "topSeller", "dealOfTheWeek", "isNew", "stock", "stockCount"],
  };
  const sections = Object.entries(groups).filter(([, keys]) => !before || keys.some(key => JSON.stringify(comparable(data[key])) !== JSON.stringify(comparable(before[key])))).map(([label]) => label);
  return { name: data.genericBrand, language, visible: data.visible, sections: sections.length ? sections : ["Medicine saved; no content changes"], translation: !!translation?.scope, created, at: Date.now() };
}
